import emojiRegex from "emoji-regex";
import { useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import Swal from "sweetalert2";
import {
  getUpdatedUserData,
  sendMessagesAiChat
} from "../../models/aichat.model";
import { UserContext } from "../../services/AppContext/appProvider";
import {
  checkForUuid,
  checkValidPage,
  getAIChatUserData,
  getLastUserDataUpdateChat,
  setAIChatUserDataLocalStorage,
  setInitialAIUserChatLocalStorage
} from "../../utilities/aiChat";
import {
  AI_CHAT_ALLOWED_MAX_WAIT_TIME,
  AI_CHAT_MESSAGE_LIMIT,
  AI_CHAT_UPDATE_INTERVAL
} from "../../utilities/constants";
import { AiBio } from "../AiBio/AiBio.component";

export const AiChat: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const [expand, setExpand] = useState<boolean>(false);
  // const [initialMessage, setInitialMessage] = useState<ChatMessage | null>(null);
  const [messageQueue, setMessageQueue] = useState<aiMessageQueue[]>([]);
  const [terminateQueue, setTerminateQueue] = useState<boolean>(false);
  const [mobileSize, setMobileSize] = useState<boolean>(false);
  const [largeScreen, setLargeScreen] = useState<boolean>(false);
  const [currentMessage, setCurrentMessage] = useState<string>("");
  const [messageResponseLoading, setMessageResponseLoading] =
    useState<boolean>(false);
  const [typingDots, setTypingDots] = useState<string>("");
  const [showAiBioModal, setShowAiBioModal] = useState<boolean>(false);
  const messageAbortRef = useRef(new AbortController());
  const messageRequestAborted = useRef<boolean>(false);

  const {
    isLoggedIn,
    showChatBox,
    setShowChatBox,
    currentPage,
    setCurrentPage,
    aiSystemMessage,
    setAiSystemMessage,
    aiMessages,
    setShowChatNotificationModal,
    setAiMessages
  } = useContext(UserContext);

  // ====================================================
  // ----- UseEffects -----

  // Track if mobile size
  // controls block full expansion and display of collapse / expand buttons
  useEffect(() => {
    const handleResize = () => {
      setMobileSize(window.innerWidth <= 768);
      setLargeScreen(window.innerWidth > 1024);
    };

    // Set initial state based on current window size
    handleResize();

    // tracks any resizes of the display window
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  // Tracks the current page - called on page change
  // See - 'aiPageControl' for more details
  // clears current message on load or page change
  useEffect(() => {
    setCurrentMessage("");
    setAiSystemMessage(null);
    setShowChatNotificationModal(false);
    const handlePageChange = () => {
      aiPageControl();
    };

    // Initial call to set the current page
    handlePageChange();
  }, [location.pathname, location.search, currentPage, setCurrentPage]);

  // Determine the current page from URL
  // Dashboard pages - retrieved from URL params
  // login, sign-up, terms-and-conditions are modals (not pages) - names are set within their own components
  // currentPage and setCurrentPage are from global context
  const aiPageControl = () => {
    let mainPage = location.pathname;
    if (mainPage === "/") {
      mainPage = "home";
    } else {
      mainPage = mainPage.replace("/", "");
      mainPage = mainPage.replace("-", "_");
    }

    let newPage = "";

    // Check for modal pages
    if (
      currentPage === "sign_up" ||
      currentPage === "login" ||
      currentPage === "terms-and-conditions"
    ) {
      newPage = currentPage;
    } else if (mainPage.includes("item")) {
      // removed item id from tracking
      newPage = "item";
    } else if (mainPage.includes("reset_password")) {
      // remove token
      newPage = "reset_password";
    } else if (mainPage.includes("setup_account")) {
      // setup account
      newPage = "setup_account";
    } else if (mainPage.includes("auth")) {
      newPage = "auth";
    } else {
      // Normally, get the main page based on pathname
      newPage = mainPage;

      // Check if there are any query parameters indicating a subpage
      const queryParams = new URLSearchParams(location.search);
      const subPage = queryParams.get("page");

      // If there's a subpage, set it as the page name
      if (subPage) {
        newPage = `${subPage}`;
      }
    }

    // Update the current page state only if it has changed
    if (newPage !== currentPage) {
      setCurrentPage(newPage);
      // clear the messages if the page is changed
      setAiMessages(null);
    }
  };

  // -------------------------------------------------------------------------
  // CONTROLLER - triggered on page change
  useEffect(() => {
    const fetchData = async () => {
      // const exclude = await checkExclude();
      // console.log("exclude", exclude);

      // 1) Check if the current page is in the list of pages to fetch messages
      const isValidPage = checkValidPage(currentPage);
      if (isValidPage) {
        // ============================================================
        // DATA PREPARATION - USER DATA
        await userDataHandler();
      } else {
        // not a valid page - no question to be asked
        clearPageAndNotification();
        return;
      }
    };

    fetchData();
  }, [isLoggedIn, currentPage]);

  // Message Queue Handler
  useEffect(() => {
    let isMounted = true; // Flag to track if the component is mounted
    messageRequestAborted.current = false; // Reset the abort flag

    // Cleanup Function: Reset the AbortController and isMounted
    const resetController = () => {
      messageAbortRef.current.abort();
      messageAbortRef.current = new AbortController();
      messageRequestAborted.current = false;
    };

    async function handleQueue() {
      if (terminateQueue) {
        if (isMounted) {
          setAiMessages(null);
          setMessageQueue([]);
          setMessageResponseLoading(false);
          messageAbortRef.current.abort();
        }
        setTerminateQueue(false);
        return; // Early exit
      }

      if (messageQueue.length > 0 && !terminateQueue) {
        setMessageResponseLoading(true);
        scrollToBottom();

        const TIMEOUT_MS = AI_CHAT_ALLOWED_MAX_WAIT_TIME;
        const timeoutId = window.setTimeout(() => {
          messageRequestAborted.current = true;
          // setTerminateQueue(true);
          setMessageResponseLoading(false);
          const updatedQueue = [...messageQueue];
          // Remove the first item from the queue
          updatedQueue.shift();
          setMessageQueue(updatedQueue);
          const newMessage = {
            system: false,
            ai: true,
            content: t("timeout_message"),
            time: Math.floor(Date.now() / 1000) // Set time as epoch
          };
          const updatedMessages = isMounted
            ? aiMessages
              ? [...aiMessages, newMessage]
              : [newMessage]
            : aiMessages;
          if (isMounted) {
            setAiMessages(updatedMessages);
            scrollToBottom();
          }
        }, TIMEOUT_MS);

        try {
          const messageResult = await sendMessagesAiChat(
            messageQueue[0].systemMessage,
            messageQueue[0].userMessage,
            currentPage,
            messageAbortRef.current.signal
          );
          clearTimeout(timeoutId);

          if (!isMounted) {
            return;
          }
          if (messageRequestAborted.current === true) {
            messageRequestAborted.current = false;
            return;
          }

          const updatedQueue = [...messageQueue];
          // Remove the first item from the queue
          updatedQueue.shift();
          if (isMounted) {
            setMessageQueue(updatedQueue);
          }
          setTypingDots("");

          setMessageResponseLoading(false);
          if (messageResult.rStatus === "error") {
            console.error(
              "Error sending message",
              messageResult.messageKey
                ? messageResult.messageKey
                : "unknown message identifier"
            );
            const newMessage = {
              system: false,
              ai: true,
              content: messageResult.message
                ? t(messageResult.message)
                : t("error_message_generic"),
              time: Math.floor(Date.now() / 1000) // Set time as epoch
            };

            const updatedMessages = isMounted
              ? aiMessages
                ? [...aiMessages, newMessage]
                : [newMessage]
              : aiMessages;

            if (isMounted) {
              setAiMessages(updatedMessages);
              scrollToBottom();
            }
          } else {
            if (messageResult.message && !terminateQueue) {
              const newMessage = {
                system: false,
                ai: true,
                content: messageResult.message,
                time: Math.floor(Date.now() / 1000) // Set time as epoch
              };
              const updatedMessages = isMounted
                ? aiMessages
                  ? [...aiMessages, newMessage]
                  : [newMessage]
                : aiMessages;

              if (isMounted) {
                setAiMessages(updatedMessages);
                scrollToBottom();
              }
            } else {
              console.error(
                "Return message has no content",
                messageResult.message
              );
              Swal.fire({
                icon: "error",
                title: "Error",
                text:
                  messageResult.message ||
                  "There was an issue sending your message. Please try again."
              });
            }
          }
        } catch (error) {
          clearTimeout(timeoutId);
          if ((error as DOMException).name === "AbortError") {
            console.log("Request aborted - timeout");
            return;
          }
        } finally {
          // no matter what - make sure that the timeout is cleared
          clearTimeout(timeoutId);
        }
      }
    }

    handleQueue();

    // Cleanup function to set isMounted to false
    return () => {
      isMounted = false;
      resetController();
    };
  }, [messageQueue, terminateQueue]);

  // USER DATA HANDLER
  const userDataHandler = async (): Promise<void> => {
    const userData: AiUserData | null = getAIChatUserData();
    // Check if logged in
    if (isLoggedIn) {
      if (userData) {
        // check if data has uuid (will set in local storage if not present from token)
        const uuidCheckResponse = checkForUuid();
        if (
          uuidCheckResponse.errorString &&
          uuidCheckResponse.errorString !== ""
        ) {
          console.error(uuidCheckResponse.errorString);
        }
        // get last updated time if data present
        const lastUpdate = getLastUserDataUpdateChat();
        if (
          lastUpdate !== null &&
          lastUpdate !== undefined &&
          typeof lastUpdate === "number"
        ) {
          const outOfDate =
            Math.floor(Date.now()) - lastUpdate > AI_CHAT_UPDATE_INTERVAL;
          if (outOfDate) {
            // logged in, but no data - get user data
            const updatedUserData = await getUpdatedUserData();
            // set data in local storage
            if (updatedUserData) {
              setAIChatUserDataLocalStorage(updatedUserData);
            }
          }
        }
      } else {
        // no data - create default data
        const updatedUserData = await getUpdatedUserData();
        // set data in local storage
        if (updatedUserData) {
          setAIChatUserDataLocalStorage(updatedUserData);
        }
      }
    } else {
      // not logged in
      // if there is data but user not logged in, can't do anything more

      // if no user_data in local storage - create default data
      if (!userData) {
        setInitialAIUserChatLocalStorage();
      }
    }
  };

  // control typing dots
  useEffect(() => {
    if (messageResponseLoading) {
      let dots = 0;
      const interval = setInterval(() => {
        setTypingDots(".".repeat(dots));
        dots = (dots + 1) % 4; // Cycle from 0 to 3
      }, 500); // Adjusted interval to 500ms for smoother dot changes

      return () => clearInterval(interval);
    } else {
      setTypingDots(""); // Clear dots when not loading
    }
  }, [messageResponseLoading]);

  // ====================================================
  // FUNCTIONS

  // logic for closing chat window
  const handleChatBoxClose = () => {
    setCurrentMessage("");
    setAiSystemMessage(null);
    setAiMessages(null);
    setShowChatBox(false);
    setTerminateQueue(true);
    messageRequestAborted.current = false;
  };

  // Note: This does not send the message
  // Logic ultimately results in addition of message to a queue that is handled
  // and sent by a useEffect above
  // Reason - best way to deal with multiple sent messages before response has been received
  // or if user prematurely closes the chat block before responses are received.
  const handleSendMessage = async () => {
    // check empty message
    if (currentMessage.trim() === "") {
      return;
    }

    const { visualMessage, APIMessage } = messageSanitize();
    if (visualMessage === null || APIMessage === null) {
      return;
    }

    if (messageQueue.length >= AI_CHAT_MESSAGE_LIMIT) {
      //add the message to the current content
      const limitMessage = {
        system: false,
        ai: true,
        content:
          "Hang tight! Your messages are on their way. Please wait a moment before sending another.",
        time: Math.floor(Date.now() / 1000) // Set time as epoch
      };
      const updatedMessages = aiMessages
        ? [...aiMessages, limitMessage]
        : [limitMessage];
      setAiMessages(updatedMessages);
      scrollToBottom();
      return;
    }

    const translatedSystemMessage = aiSystemMessage ? t(aiSystemMessage) : null;
    const messageForQueue: aiMessageQueue = {
      systemMessage: translatedSystemMessage,
      userMessage: APIMessage
    };

    //format user message
    const message = {
      system: false,
      ai: false,
      content: visualMessage.trim(),
      time: Math.floor(Date.now() / 1000) // Set time as epoch
    };

    //add the message to the current content
    const updatedMessages = aiMessages ? [...aiMessages, message] : [message];
    setAiMessages(updatedMessages);
    scrollToBottom();

    setMessageQueue((prevQueue) => [...prevQueue, messageForQueue]);
    // clear the input field
    setCurrentMessage("");
  };

  // convert chat times from epoch to date string - only as time (no date)
  function convertEpochTimeOnly(time: number): string {
    return new Date(time * 1000).toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false
    });
  }

  // Sanitize message Content - visual and for Data Send
  const messageSanitize = (): {
    visualMessage: string | null;
    APIMessage: string | null;
  } => {
    let rawMessage = currentMessage;

    // Split into lines by newline chars
    const lines = rawMessage.split(/\r?\n/);

    // Clean each line: trim and collapse internal spaces/tabs
    const cleanedLines = lines.map((line) =>
      line.trim().replace(/[ \t]+/g, " ")
    );

    // Join lines with '\n' to preserve paragraphs and empty lines
    let visualMessage = cleanedLines.join("\n");

    // Remove ASCII control characters except tab (0x09), newline (0x0A), carriage return (0x0D)
    visualMessage = visualMessage.replace(
      /[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g,
      ""
    );

    // Collapse 3 or more newlines into exactly two newlines, preserving one empty line
    visualMessage = visualMessage.replace(/\n{3,}/g, "\n\n");

    const regex = emojiRegex();

    // Prepare APIMessage by removing all newlines and extra spaces, leaving a single space separator
    let APIMessage = visualMessage
      .replace(/\n/g, " ")
      .replace(/\s+/g, " ")
      .replace(regex, "") // Remove emojis
      .trim();

    // If after cleaning message is empty, return nulls
    if (APIMessage === "") {
      return { visualMessage: null, APIMessage: null };
    }

    return { visualMessage, APIMessage };
  };

  // scroll to bottom of chat window after new message
  function scrollToBottom() {
    // Smoothly scroll to the bottom of the messages area
    setTimeout(() => {
      const messagesArea = document.querySelector(
        ".chat-block"
      ) as HTMLElement | null;
      if (messagesArea instanceof HTMLElement) {
        messagesArea.scrollTo({
          top: messagesArea.scrollHeight,
          behavior: "smooth"
        });
      }
    }, 0);
  }

  const clearPageAndNotification = () => {
    setShowChatNotificationModal(false);
    setAiSystemMessage(null);
    setAiMessages(null);
    // setCurrentPage("");
  };

  return (
    <div>
      {showChatBox && (
        // Overlay
        <div
          className="top-50 modal modal-open fixed inset-0 z-50 flex items-center justify-center"
          style={{ zIndex: 1000 }}
        >
          {/* Chat Block */}
          <div
            className={`relative ${
              expand || mobileSize ? "m-2 mb-10 h-[85%] w-[95%]" : "h-3/4 w-3/4"
            } no-scrollbar flex flex-col overflow-hidden rounded-lg border border-neutral-500 bg-white p-2 shadow-lg`}
          >
            <div className="grid h-full grid-cols-5 gap-2">
              {/* ---------------------------------------------------------------------------------------------------------------------- */}
              {/* AI BIO Section*/}
              {largeScreen && (
                <div className="no-scrollbar col-span-2 overflow-auto border-r-2 border-r-neutral-500">
                  <AiBio
                    largeScreen={true}
                    isMobile={false}
                    expand={expand}
                    setShowAiBio={setShowAiBioModal}
                  />
                </div>
              )}
              {/* ---------------------------------------------------------------------------------------------------------------------- */}
              {/* Message Section */}
              <div
                className={`no-scrollbar ${!largeScreen ? "col-span-5" : "col-span-3"} flex h-full flex-col overflow-auto`}
              >
                {/* ---------------------------------------------------------------------------------------------------------------------- */}
                {/* TOP SECTION - IMAGE, NAME AND BUTTONS */}
                <div className="border-b-chat-color mb-2 grid w-full grid-cols-6 items-center gap-3 border-b-2 p-1">
                  <div className="col-span-5 flex items-center gap-2">
                    <img
                      className={`h-16 min-h-16 w-16 min-w-16 rounded-full md:h-16 md:min-h-16 md:w-16 md:min-w-16 ${!largeScreen ? "cursor-pointer" : "cursor-default"}`}
                      src="/assets/images/Jayda_ai.webp"
                      alt="ai_chat_image"
                      onClick={() => {
                        setShowAiBioModal(!showAiBioModal);
                      }}
                    />
                    <div
                      className={`flex flex-col ${!largeScreen ? "cursor-pointer" : "cursor-default"}`}
                      onClick={() => {
                        !largeScreen && setShowAiBioModal(!showAiBioModal);
                      }}
                    >
                      <h1 className={`text-sm font-bold md:text-lg`}>
                        {t("AI Chat Heading")}
                      </h1>
                      {!largeScreen && (
                        <h6 className="text-xs">
                          {t("ai_profile_view_message")}
                        </h6>
                      )}
                    </div>
                  </div>

                  <div className="absolute right-2 top-0 col-span-1 flex items-center justify-end gap-4">
                    {/* Expand / Collapse Block Button - Always on the far right */}
                    {!mobileSize && (
                      <img
                        className="h-4 min-h-4 w-4 min-w-4 cursor-pointer"
                        src={`${
                          expand
                            ? "/assets/images/icons/collapse.svg"
                            : "/assets/images/icons/expand.svg"
                        }`}
                        alt="ai_chat_image"
                        onClick={() => {
                          setExpand(!expand);
                        }}
                      />
                    )}
                    {/* Close Chat Button */}
                    <button
                      className="text-3xl font-bold hover:text-secondary"
                      onClick={() => {
                        handleChatBoxClose();
                      }}
                    >
                      &times;
                    </button>
                  </div>
                </div>
                {/* ---------------------------------------------------------------------------------------------------------------------- */}

                {/* MESSAGES AREA */}
                <div className="no-scrollbar chat-block mx-2 flex flex-grow flex-col gap-2 overflow-y-scroll">
                  {/* ---------------------------------------------------------------------------------------------------------------------- */}
                  {/* FIRST MESSAGE - only if no system message*/}
                  {!aiSystemMessage && (
                    <div
                      className={`bg-chat-color ${mobileSize ? "w-4/5" : "min-w-[40%] max-w-[60%]"} justify-self-start rounded-2xl p-3`}
                    >
                      <div>
                        <div className="flex flex-col items-start">
                          <div className="">{t("Ai Chat Initial Message")}</div>
                          <div className="mt-1 self-end text-sm text-gray-400">
                            {convertEpochTimeOnly(
                              Math.floor(Date.now() / 1000)
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  {/* ---------------------------------------------------------------------------------------------------------------------- */}
                  {/* Messages */}
                  <div className="mb-2">
                    {aiMessages?.map((message, index) => (
                      <div
                        key={index}
                        className={`bg-chat-color mt-4 w-auto ${mobileSize ? "min-w-[60%] max-w-[80%]" : "min-w-[40%] max-w-[60%]"} rounded-2xl p-2 ${
                          message.ai ? "justify-self-start" : "ml-auto"
                        }`}
                      >
                        <div
                          className="break-words"
                          style={{ whiteSpace: "pre-wrap" }}
                        >
                          {t(message.content)}
                        </div>
                        <div className="mt-1 justify-self-end text-sm text-gray-400">
                          {message.time
                            ? convertEpochTimeOnly(message.time)
                            : convertEpochTimeOnly(
                                Math.floor(Date.now() / 1000)
                              )}
                        </div>
                      </div>
                    ))}
                  </div>
                  {/* Loading Message Block */}
                  <div className="mb-2">
                    {messageResponseLoading && (
                      <div className="bg-chat-color mt-4 w-3/5 justify-self-start rounded-2xl p-2">
                        <div className="break-words">Typing {typingDots}</div>
                      </div>
                    )}
                  </div>
                </div>
                {/* ---------------------------------------------------------------------------------------------------------------------- */}
                {/* Bottom Section - Message Input and Send Button */}
                <div className="align-self-end m-1 mt-auto flex items-center justify-end gap-2">
                  <textarea
                    className={`no-scrollbar bg-chat-input-color w-full resize-none rounded-lg border border-secondary p-2`}
                    placeholder={t("Type your message")}
                    rows={1}
                    value={currentMessage}
                    style={{
                      maxHeight: "150px",
                      overflowY: "auto",
                      WebkitOverflowScrolling: "touch"
                    }}
                    onChange={(e) => {
                      const value = e.target.value;
                      setCurrentMessage(value);
                    }}
                    onInput={(e) => {
                      const target = e.target as HTMLTextAreaElement;
                      target.style.height = "auto";
                      target.style.height = `${Math.min(
                        target.scrollHeight,
                        150
                      )}px`;
                    }}
                  />
                  <button
                    className={`bg-chat-input-color btn btn-outline btn-secondary btn-sm flex-shrink-0 rounded-lg px-4 py-2 hover:text-white`}
                    style={{
                      backgroundColor: !currentMessage.trim() ? "" : undefined,
                      color: !currentMessage.trim() ? "#a9a9a9" : undefined
                    }}
                    onClick={() => {
                      if (currentMessage.trim()) {
                        console.log(currentMessage);
                        handleSendMessage();
                        const textarea = document.querySelector(
                          "textarea"
                        ) as HTMLTextAreaElement | null;
                        if (textarea) {
                          textarea.style.height = "auto"; // Reset height to default
                        }
                      }
                    }}
                    disabled={!currentMessage.trim()}
                  >
                    {t("Send")}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* <AiBioModal /> */}
      {!largeScreen && showAiBioModal && (
        // Overlay
        <div
          className="top-50 fixed inset-0 z-50 flex items-center justify-center gap-2 bg-black bg-opacity-50"
          style={{ zIndex: 1000 }}
        >
          <div
            className={` ${mobileSize ? "h-5/6 w-full" : "h-3/4 w-3/4"} no-scrollbar m-4 mb-2 mt-2 overflow-auto rounded-lg bg-success`}
          >
            <AiBio
              largeScreen={false}
              isMobile={mobileSize}
              setShowAiBio={setShowAiBioModal}
              expand={expand}
            />
          </div>
        </div>
      )}
    </div>
  );
};
